import React, { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { ulid } from "ulid";
import { GetlifeContext } from "../../../context/GetLifeContext.context";
import PricingCandidatesRepository from "../../../api/request/PricingCandidates/PricingCandidates.service";
import { isNativeMode } from "../../../native/nativeMode";
import { Capabilities } from "../../../api/request/PricingCandidates/types";
import CandidateUpsell from "./CandidateUpsell";

/** The server chooses the flow BEFORE either legacy gateway (including direct payment URLs) mounts. */
export default function CandidateModeBoundary({ children }: { children: ReactNode }) {
  const { leadId, token } = useContext(GetlifeContext);
  const { pathname } = useLocation();
  const session = useMemo(() => ({ repository: new PricingCandidatesRepository(leadId, token), key: ulid() }), [leadId, token]);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{
    session: typeof session; path: string; revision: number; capabilities?: Capabilities; failed?: boolean;
  }>();
  useEffect(() => {
    // Organic native: no hay "candidate mode" (eso es del monolito, otro origen →
    // neutralizado). No se consulta pricing-capabilities; el upsell lo sirve el
    // gateway (NativeUpsell en PaymentGatewayV2). Ver el render native más abajo.
    if (isNativeMode()) {
      return;
    }
    let current = true;
    session.repository.capabilities().then(capabilities => {
      if (current) setResult({ session, path: pathname, revision, capabilities });
    }).catch(() => { if (current) setResult({ session, path: pathname, revision, failed: true }); });
    return () => { current = false; };
  }, [session, pathname, revision]);
  // Only cross-tab history changes re-check the mode. A window "focus" listener is NOT used here:
  // the Stripe card iframes blur/focus the window on every click, which unmounted the whole payment step.
  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === `life5:pricing-candidates:v1:${leadId}`) setRevision(value => value + 1);
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, [leadId]);
  // Organic native: sin candidate mode; renderiza el flujo (PaymentGatewayV2 →
  // NativeUpsell, que pide el upsell al gateway).
  if (isNativeMode()) {
    return <>{children}</>;
  }
  if (result?.session !== session || result.path !== pathname || result.revision !== revision) {
    return <p role="status">Consultando tus opciones…</p>;
  }
  if (result.failed || !result.capabilities) return <section role="alert">
    <p>No podemos consultar tus opciones en este momento.</p>
    <button onClick={() => setRevision(value => value + 1)}>Volver a consultar</button>
  </section>;
  if (result.capabilities.mode === "legacy") return <>{children}</>;
  return <CandidateUpsell key={session.key} leadId={leadId} repository={session.repository} capabilities={result.capabilities} />;
}
